import React, {memo, useCallback, useEffect, useMemo, useState} from "react"
import {PasswordContainer} from "../../../../components/PasswordContainer"
import {SettingsPageContainer} from "../../../../components/Settings/SettingsPageContainer"
import {SettingInputComponent} from "../../../../components/Settings/SettingInputComponent"
import {useRPC} from "../../../../hooks/rpc"
import {useMessage} from "../../../../hooks/message"
import {useHistory} from "react-router-dom"
import {useLocalization} from "../../../../hooks/localization"
import {useDispatch, useSelector} from "react-redux"
import {SettingSelectComponent} from "../../../../components/Settings/SettingSelectComponent"
import {SelectItem} from "../../../../components/Select"
import {SettingCheckBoxComponent} from "../../../../components/Settings/SettingCheckboxComponent"
import {useKeyboard} from "../../../../hooks/keyboard"
import {DateState} from "../../../../store/control/state"
import {useQuestion} from "../../../../hooks/question"
import {setTimezoneOffsetAction} from "../../../../store/control/actions"
import "./index.scss"

const BACK_URL = "/settings"
const numberControlRegExp = /^-?\d*\.?\d*$/
const checkNumberControl = (value: string) => numberControlRegExp.test(value)

export const SetDateTimeScreen = memo(function SetDateTimeScreen() {
    const {t} = useLocalization()
    const [rpc] = useRPC()
    const dispatch = useDispatch()
    const {success, failure} = useMessage()
    const history = useHistory()
    const question = useQuestion()
    const timezoneList = useSelector(state => state.control.timezoneList)
    const timezoneOffset = useSelector(state => state.control.timezoneOffset)
    const [timezoneOffsetSetter, setTimezoneOffsetSetter] = useState<DateState>(timezoneOffset || null)
    const [setActiveNumpadInput, renderNumpadKeyboard] = useKeyboard("numpad-with-minus", true)
    const [pickedDateString, setPickedDateString] = useState("")
    const [pickedTimeString, setPickedTimeString] = useState("")
    const [manuelTimezoneOffset, setManuelTimezoneOffset] = useState(
        timezoneOffsetSetter?.is_manual_time_offset || false
    )
    const [timezoneOffsetInMinutes, setTimezoneOffsetInMinutes] = useState(
        String(timezoneOffsetSetter?.timezone_offset_in_minutes) || ""
    )

    const customizedTimezoneList = useMemo<SelectItem<string>[]>(() => {
        const list = []
        for (let i = 0; i < timezoneList.length; i++) {
            const value = timezoneList[i]?.timezone
            list.push({
                value,
                label: value
            })
        }
        return list
    }, [timezoneList])

    const [timezone, setTimezone] = useState(
        timezoneOffsetSetter != null
            ? customizedTimezoneList.find(value => value?.value === timezoneOffsetSetter?.timezone)
            : customizedTimezoneList[0]
    )
    const onBlur = useCallback(() => setActiveNumpadInput(null), [setActiveNumpadInput])

    const onBackPressed = useCallback(() => {
        history.push(BACK_URL)
    }, [history])

    const onSavePressed = useCallback(() => {
        if (pickedDateString === "") {
            failure(t("pls_enter_a_valid_date"))

            return
        }

        const pickedDate = new Date(pickedDateString)
        if (!(pickedDate instanceof Date) || isNaN(pickedDate as unknown as number)) {
            failure(t("pls_enter_a_valid_date"))
        }

        const offset = -pickedDate.getTimezoneOffset()
        const dateUTC = pickedDate.getTime() / 1000 - offset * 60
        const [pickedHourString, pickedMinuteString] = pickedTimeString.split(":")
        const calculatedTime = Number(pickedHourString) * 60 * 60 + Number(pickedMinuteString) * 60
        rpc.updateTimeAndWeather(dateUTC + calculatedTime, offset)
            .then(() => {
                success(t("successfully_saved"))
                onBackPressed()
            })
            .catch(failure)
    }, [t, pickedDateString, pickedTimeString, rpc, onBackPressed, failure, success])

    const onSavePressedFromTimezone = useCallback(() => {
        if (!checkNumberControl(timezoneOffsetInMinutes)) {
            failure(t("please_enter_a_valid_value"))
            return
        }
        const timezoneState: DateState = {
            timezone: timezone?.value,
            timezone_offset_in_minutes: manuelTimezoneOffset ? Number(timezoneOffsetInMinutes) : 0,
            is_manual_time_offset: manuelTimezoneOffset
        }
        question(t("you_must_restart_your_device_after_this_process"), () => {
            rpc.updateTimezone(timezoneState)
                .then(() => success(t("rebooting")))
                .catch(failure)
        })
    }, [t, question, failure, success, rpc, timezoneOffsetInMinutes, manuelTimezoneOffset, timezone?.value])

    const buttons = useMemo(
        () => [
            {
                text: t("save_timezone"),
                className: "blue",
                onClick: onSavePressedFromTimezone
            },
            {
                text: t("save"),
                className: "green",
                onClick: onSavePressed
            },
            {
                text: t("cancel"),
                className: "red",
                url: BACK_URL
            }
        ],
        [t, onSavePressedFromTimezone, onSavePressed]
    )

    useEffect(() => {
        setManuelTimezoneOffset(timezoneOffsetSetter?.is_manual_time_offset)
        setTimezone(
            timezoneOffsetSetter != null
                ? customizedTimezoneList.find(value => value?.value === timezoneOffsetSetter?.timezone)
                : customizedTimezoneList[0]
        )
        setTimezoneOffsetInMinutes(String(timezoneOffsetSetter?.timezone_offset_in_minutes))
    }, [customizedTimezoneList, timezoneOffsetSetter])

    useEffect(() => {
        rpc.getTimezoneOffset().then(data => {
            dispatch(setTimezoneOffsetAction(data))
            setTimezoneOffsetSetter(data)
        })
    }, [dispatch, rpc])

    return (
        <PasswordContainer requiredRole="admin" isFullscreen={true}>
            <SettingsPageContainer
                className="service-edit"
                buttons={buttons}
                backUrl={BACK_URL}
                title={t("set_date_and_time")}
            >
                <div className="content-divider">
                    <div className="content-wrapper">
                        <SettingInputComponent
                            placeholder={t("set_date")}
                            label={t("date")}
                            value={pickedDateString}
                            setValue={setPickedDateString}
                            setActiveInput={null}
                            type="date"
                        />
                        <SettingInputComponent
                            placeholder={t("set_time")}
                            label={t("time")}
                            value={pickedTimeString}
                            setValue={setPickedTimeString}
                            setActiveInput={null}
                            type="time"
                        />
                    </div>
                    <div className="content-wrapper">
                        <SettingSelectComponent
                            label={t("timezone")}
                            options={customizedTimezoneList}
                            onChange={setTimezone}
                            value={timezone}
                        />
                        <SettingCheckBoxComponent
                            label="Manuel Offset"
                            value={manuelTimezoneOffset}
                            set={() => setManuelTimezoneOffset(p => !p)}
                        />
                        {renderIf(manuelTimezoneOffset)(() => (
                            <SettingInputComponent
                                placeholder={t("pls_enter_offset")}
                                label={t("offset_minute")}
                                value={timezoneOffsetInMinutes}
                                setValue={setTimezoneOffsetInMinutes}
                                setActiveInput={setActiveNumpadInput}
                                onBlur={onBlur}
                            />
                        ))}
                    </div>
                </div>
            </SettingsPageContainer>
            {renderNumpadKeyboard()}
        </PasswordContainer>
    )
})
